import React, { forwardRef, useEffect, useRef, useState, useCallback } from "react";
import "react-quill/dist/quill.snow.css";
import "../../scss/quill-editor-enhanced.scss";

// Enhanced polyfill for findDOMNode to support React 19 and suppress warnings
if (typeof window !== "undefined") {
  // First, try to patch react-dom before ReactQuill loads
  try {
    const reactDom = require("react-dom");

    // Create our complete replacement (no warnings)
    const polyfillFindDOMNode = component => {
      // Complete polyfill implementation without calling original
      if (!component) return null;

      // Direct DOM element
      if (component.nodeType === 1) return component;

      // React ref
      if (component.current && component.current.nodeType === 1) return component.current;

      // React component instance - try multiple approaches
      if (component._reactInternalFiber) {
        const fiber = component._reactInternalFiber;
        if (fiber.child && fiber.child.stateNode) return fiber.child.stateNode;
        if (fiber.stateNode) return fiber.stateNode;
      }

      // React 18+ fiber structure
      if (component._reactInternals) {
        const fiber = component._reactInternals;
        if (fiber.child && fiber.child.stateNode) return fiber.child.stateNode;
        if (fiber.stateNode) return fiber.stateNode;
      }

      // For Quill specifically, try finding the editor element
      if (component.querySelector) {
        const editor = component.querySelector(".ql-editor");
        if (editor) return editor;
        return component.querySelector(".quill");
      }

      // Last resort - return the component itself if it looks like a DOM element
      if (component.tagName) return component;

      return null;
    };

    // Override findDOMNode
    reactDom.findDOMNode = polyfillFindDOMNode;

    // Also patch the default export if it exists
    if (reactDom.default && reactDom.default.findDOMNode) {
      reactDom.default.findDOMNode = polyfillFindDOMNode;
    }

    // As a final fallback, suppress ReactQuill-specific findDOMNode warnings
    const originalConsoleError = console.error;
    const originalConsoleWarn = console.warn;

    console.error = (...args) => {
      if (
        args[0] &&
        typeof args[0] === "string" &&
        args[0].includes("findDOMNode is deprecated") &&
        (args[0].includes("ReactQuill") || new Error().stack.includes("ReactQuill"))
      ) {
        return; // Suppress ReactQuill findDOMNode warnings
      }
      originalConsoleError.apply(console, args);
    };

    console.warn = (...args) => {
      if (
        args[0] &&
        typeof args[0] === "string" &&
        args[0].includes("findDOMNode is deprecated") &&
        (args[0].includes("ReactQuill") || new Error().stack.includes("ReactQuill"))
      ) {
        return; // Suppress ReactQuill findDOMNode warnings
      }
      originalConsoleWarn.apply(console, args);
    };
  } catch (e) {
    console.warn("Could not patch ReactDOM.findDOMNode:", e);
  }
}

// Note: quill-editor-enhanced.scss includes dropdown display styles and editor appearance

/**
 * QuillEditor component with custom fonts and sizes
 *
 * All formatting now uses inline styles for client app compatibility:
 * - Fonts: Inline styles (e.g., style="font-family: 'Asap-Regular', Arial, sans-serif")
 * - Sizes: Inline styles (e.g., style="font-size: 16px")
 * - Other formatting: Quill's default inline styles
 */
const QuillEditor = forwardRef(({ readOnly = false, defaultValue = "", onTextChange, onSelectionChange }, ref) => {
  const [value, setValue] = useState(defaultValue || "");
  const [charCount, setCharCount] = useState(0);
  const [wordCount, setWordCount] = useState(0);
  const [ReactQuill, setReactQuill] = useState(null);
  const quillRef = useRef(null);
  const containerRef = useRef(null);
  const [isQuillConfigured, setIsQuillConfigured] = useState(false);

  // Count characters and words
  const updateCounts = useCallback(content => {
    const text = content.replace(/<[^>]*>/g, ""); // Strip HTML
    const chars = text.length;
    const words = text.trim() === "" ? 0 : text.trim().split(/\s+/).length;
    setCharCount(chars);
    setWordCount(words);
  }, []);

  // Dynamically import react-quill and configure custom fonts
  useEffect(() => {
    if (typeof window !== "undefined" && !isQuillConfigured) {
      import("react-quill").then(module => {
        const { Quill } = module;

        // IMPORTANT: Force clear any existing registrations first
        try {
          // Configure fonts and sizes with inline styles
          const FontAttributor = Quill.import("attributors/style/font");
          const SizeAttributor = Quill.import("attributors/style/size");

          // Clear any existing whitelists
          delete FontAttributor.whitelist;
          delete SizeAttributor.whitelist;

          // Set our custom whitelists
          FontAttributor.whitelist = ["Asap-Regular", "serif", "monospace"];
          SizeAttributor.whitelist = [
            "8px",
            "9px",
            "10px",
            "11px",
            "12px",
            "14px",
            "16px",
            "18px",
            "24px",
            "30px",
            "36px",
            "48px",
            "60px",
            "72px",
            "96px",
          ];

          // Force register with override - use specific format names
          Quill.register("formats/font", FontAttributor, true);
          Quill.register("formats/size", SizeAttributor, true);

          console.log("Quill custom attributors registered:", {
            font: FontAttributor.whitelist,
            size: SizeAttributor.whitelist,
          });
        } catch (error) {
          console.error("Error configuring Quill attributors:", error);
        }

        setIsQuillConfigured(true);
        setReactQuill(() => module.default);
      });
    }
  }, [isQuillConfigured]);

  // Initialize with default value and update counts
  useEffect(() => {
    if (defaultValue && defaultValue !== value) {
      setValue(defaultValue);
      updateCounts(defaultValue);
    }
  }, [defaultValue, value, updateCounts]);

  // Update counts when value changes (including initial load)
  useEffect(() => {
    updateCounts(value);
  }, [value, updateCounts]);

  // Handle content change
  const handleChange = useCallback(
    (content, delta, source, editor) => {
      setValue(content);
      if (onTextChange) {
        onTextChange(content);
      }
    },
    [onTextChange]
  );

  // Handle selection change
  const handleSelectionChange = useCallback(
    (range, source, editor) => {
      if (onSelectionChange && range) {
        const selectedText = editor.getText(range.index, range.length);
        onSelectionChange(selectedText);
      }
    },
    [onSelectionChange]
  );

  // Toolbar configuration - configured to use inline styles
  const modules = {
    toolbar: [
      [{ header: [1, 2, 3, 4, false] }],
      ["bold", "italic", "underline", "strike"],
      [{ script: "sub" }, { script: "super" }],
      [{ font: ["Asap-Regular", "serif", "monospace"] }],
      [
        {
          size: [
            "8px",
            "9px",
            "10px",
            "11px",
            "12px",
            "14px",
            "16px",
            "18px",
            "24px",
            "30px",
            "36px",
            "48px",
            "60px",
            "72px",
            "96px",
          ],
        },
      ],
      [{ color: [] }, { background: [] }],
      ["clean"],
      [{ align: [] }],
      [{ list: "ordered" }, { list: "bullet" }],
      [{ indent: "-1" }, { indent: "+1" }],
      ["blockquote"],
      ["link"],
      ["code-block"],
      ["image"],
    ],
  };

  // Formats configuration
  const formats = [
    "header",
    "font",
    "size",
    "bold",
    "italic",
    "underline",
    "strike",
    "blockquote",
    "list",
    "bullet",
    "indent",
    "link",
    "image",
    "color",
    "background",
    "align",
    "script",
    "code-block",
  ];

  // Show loading state while ReactQuill is loading
  if (!ReactQuill) {
    return (
      <div
        ref={containerRef}
        style={{
          border: "1px solid #ccc",
          borderRadius: "4px",
          padding: "20px",
          textAlign: "center",
          backgroundColor: "#f8f9fa",
        }}
      >
        Loading editor...
      </div>
    );
  }

  return (
    <div style={{ border: "1px solid #ccc", borderRadius: "4px" }}>
      <ReactQuill
        ref={el => {
          quillRef.current = el;
          if (ref) {
            if (typeof ref === "function") {
              ref(el);
            } else {
              ref.current = el;
            }
          }
        }}
        theme="snow"
        value={value}
        onChange={handleChange}
        onSelectionChange={handleSelectionChange}
        readOnly={readOnly}
        modules={modules}
        formats={formats}
        placeholder="Write your content here..."
        style={{
          backgroundColor: readOnly ? "#f8f9fa" : "white",
        }}
      />
      <div className="editor-counter">
        Characters: {charCount} | Words: {wordCount}
      </div>
    </div>
  );
});

QuillEditor.displayName = "QuillEditor";

export default QuillEditor;
